import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, ButtonTypes } from '@eika/button';

import { PaymentRetryContext } from '../../../../components/transaction/PaymentRetry';
import TransactionResultInfo from '../../../../components/transaction/TransactionResultInfo';
import type { EFakturaCreditor, EFakturaGivenPermission } from '../../../../generated-types';
import useShowPaymentResult from '../../../../hooks/useShowPaymentResult';
import useTranslation from '../../../../hooks/useTranslation';
import SigningContextFlow from '../../../../signingContext/SigningContextFlow';
import { useSigningContext } from '../../../../signingContext/signingContext';
import { Actions } from '../../../../signingContext/signingTypes';
import createUpdatePermissionRequest from '../../form/createUpdatePermissionRequest';
import { AmountSelectedType } from '../../form/state';
import useFormContext from '../../form/useFormContext';
import useEfakturaPermissionId from '../../hooks/useEfakturaPermissionId';
import routes from '../../routes';
import arraysEqual from '../../utils/arraysEqual';
import AmountSelectorsSkeletons from '../AmountSelectors/AmountSelectorsSkeletons';
import EmptyCard from './EmptyCard';
import UpdateGivenPermission from './UpdateGivenPermission';

import './UpdatePermission.scss';

const UpdatePermission: React.FC = () => {
    const translated = useTranslation();
    const efakturaPermissionId = useEfakturaPermissionId();
    const navigate = useNavigate();
    const { givenEfakturas, isLoading } = useFormContext();

    const [amountSelected, setAmountSelected] = useState<AmountSelectedType>('all');
    const [selected, setSelected] = useState<EFakturaCreditor[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>();

    const [isSettingCurrentUser, setIsSettingCurrentUser] = useState<boolean>(true);

    const targetUser = useRef<EFakturaGivenPermission | undefined>();

    useEffect(() => {
        if (!efakturaPermissionId) {
            navigate(routes.home);
        }
    }, [efakturaPermissionId, navigate]);

    useEffect(() => {
        if (givenEfakturas.data) {
            targetUser.current = givenEfakturas.data?.find((x) => x.id.value === efakturaPermissionId?.value);
        }
        setIsSettingCurrentUser(false);
    }, [efakturaPermissionId, givenEfakturas.data]);

    useEffect(() => {
        if (targetUser.current) {
            setAmountSelected(!targetUser.current?.selectedIssuers ? 'all' : 'some');
            setSelected(targetUser.current?.selectedIssuers ?? []);
        }
    }, [targetUser]);

    const {
        signingOperationPending,
        showPaymentRetry,
        startSigning,
        signingStatus,
        signingError,
        handleCancelSigningFlow,
        dispatch,
    } = useSigningContext(createUpdatePermissionRequest);

    const showPaymentResult = useShowPaymentResult();

    const onClose = () => dispatch({ type: Actions.RESET });

    const validateLocalState = useCallback(() => {
        if (!targetUser.current) {
            return translated.eFakturaSharing.validationErrors.userNotFound;
        }
        if (amountSelected === 'some' && (!selected || selected?.length === 0)) {
            return translated.eFakturaSharing.validationErrors.selected.tooFewSelected;
        }

        return undefined;
    }, [amountSelected, selected, translated.eFakturaSharing.validationErrors]);

    const hasChanged = useMemo(() => {
        const hasIssuers = (targetUser.current?.selectedIssuers ?? []).length > 0;
        const isSameArray = arraysEqual<EFakturaCreditor[], EFakturaCreditor>(
            targetUser.current?.selectedIssuers ?? [],
            selected,
            (a, b) => a.creditorId.value.localeCompare(b.creditorId.value),
        );
        const targetAmountSelected = hasIssuers ? 'some' : 'all';

        return (
            (hasIssuers && targetAmountSelected === 'some' && amountSelected === 'some' && !isSameArray) ||
            targetAmountSelected !== amountSelected
        );
    }, [targetUser, selected, amountSelected]);

    const onSubmit = async () => {
        const error = validateLocalState();
        setErrorMessage(error);
        if (!error && efakturaPermissionId && hasChanged && targetUser.current) {
            startSigning({
                id: efakturaPermissionId,
                creditors: selected,
                customerNumber: targetUser.current.id,
                disponentName: targetUser.current.disponentName,
            });
        }
    };

    if (showPaymentRetry) {
        return <PaymentRetryContext />;
    }

    if (showPaymentResult) {
        return (
            <TransactionResultInfo
                type="efaktura-sharing"
                status={signingStatus}
                error={signingError}
                retry={signingError ? handleCancelSigningFlow : onClose}
                onClose={onClose}
                onRetryManually={() => dispatch({ type: Actions.CLEAR_ERROR })}
                successButtons
            />
        );
    }

    if (isLoading) {
        return <AmountSelectorsSkeletons />;
    }

    if (!targetUser.current && !isLoading && !isSettingCurrentUser) {
        return (
            <EmptyCard title={translated.eFakturaSharing.userNotFound}>
                <Button buttonType={ButtonTypes.STANDARD_POSITIVE} onClick={() => navigate(routes.home)}>
                    {translated.ok}
                </Button>
            </EmptyCard>
        );
    }

    return (
        <SigningContextFlow>
            {targetUser.current && (
                <UpdateGivenPermission
                    user={targetUser.current}
                    onSubmit={onSubmit}
                    amountSelected={amountSelected}
                    setAmountSelected={setAmountSelected}
                    selected={selected}
                    setSelected={setSelected}
                    hasChanged={hasChanged}
                    error={errorMessage}
                    setErrorMessage={setErrorMessage}
                    locked={signingOperationPending}
                />
            )}
        </SigningContextFlow>
    );
};

export default UpdatePermission;
